import { useCallback, useEffect, useRef, useState } from "react";
import { getOpsRun, cancelOpsRun } from "../api/opsService";
import { showApiError, showToast } from "../utils/toast";

const TERMINAL = ["done", "failed", "cancelled"];

// Poll fast at first so a short run feels instant, then back off.
const FAST_TICKS = 10;
const FAST_DELAY = 1000;
const SLOW_DELAY = 3000;
// ~30 minutes of polling. Past that we stop and tell the user to come back —
// the run keeps going on the worker either way.
const MAX_TICKS = 620;

// Drives one queued ops run: start it, poll for progress, surface the result.
//
// Ranged ops used to run inside the HTTP request and 504 after a minute. They
// now answer 202 with a run_id and execute on the queue, so the page has to
// poll. `storageKey` persists the in-flight run id, which is what lets a page
// refresh re-attach to a back-fill instead of losing it.
export function useOpsRun(storageKey) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const timerRef = useRef(null);
  const stoppedRef = useRef(false);
  const runIdRef = useRef(null);

  const clear = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Private mode / blocked site data — the run still finishes server-side.
    }
  }, [storageKey]);

  const poll = useCallback(
    async (runId, tick) => {
      if (stoppedRef.current) return;

      let res;
      try {
        res = await getOpsRun(runId);
      } catch (err) {
        clear();
        setBusy(false);
        setProgress(null);
        // A 404 means the stored id belongs to another firm or was pruned —
        // a stale key from a previous login, not something to alarm anyone with.
        if (err?.status !== 404) showApiError(err);
        return;
      }

      const data = res?.data || {};

      if (TERMINAL.includes(data.state)) {
        clear();
        runIdRef.current = null;
        setResult(res);
        setProgress(null);
        setBusy(false);
        showToast(data.state === "done" ? "success" : "warning", res.message);
        return;
      }

      setProgress(data);

      if (stoppedRef.current) return;

      if (tick > MAX_TICKS) {
        setBusy(false);
        showToast("info", "Still running in the background. Reopen this page to check on it.");
        return;
      }

      timerRef.current = setTimeout(
        () => poll(runId, tick + 1),
        tick < FAST_TICKS ? FAST_DELAY : SLOW_DELAY,
      );
    },
    [clear],
  );

  // Re-attach to a run still in flight from a previous visit. This is the
  // payoff for keeping runs in a table rather than the cache.
  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem(storageKey);
    } catch {
      stored = null;
    }
    if (stored) {
      runIdRef.current = stored;
      setBusy(true);
      poll(stored, 0);
    }

    return () => {
      // Without this, navigating away leaves a timer hammering the API.
      stoppedRef.current = true;
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = useCallback(
    async (startFn, payload) => {
      setBusy(true);
      setResult(null);
      setProgress(null);
      try {
        const res = await startFn(payload);
        const runId = res?.data?.run_id;

        if (!runId) {
          // A synchronous op (the payments overdue sweep) — nothing to poll.
          setResult(res);
          setBusy(false);
          showToast("success", res?.message || "Done");
          return;
        }

        runIdRef.current = runId;
        try {
          localStorage.setItem(storageKey, runId);
        } catch {
          // Non-fatal: we just lose refresh-reattach for this run.
        }
        setProgress(res.data);
        poll(runId, 0);
      } catch (err) {
        setBusy(false);
        setProgress(null);
        showApiError(err);
      }
    },
    [poll, storageKey],
  );

  const cancel = useCallback(async () => {
    if (!runIdRef.current) return;
    try {
      await cancelOpsRun(runIdRef.current);
      // The next poll picks up the cancelled state and settles the UI.
    } catch (err) {
      showApiError(err);
    }
  }, []);

  return { busy, progress, result, start, cancel };
}
